'use client';

import { Skeleton } from '@workspace/ui/components/skeleton';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { BitLockedCard } from '../../bits/components/BitLockedCard';
import { BitCard } from '../../bits/components/BitCard';
import { RefObject } from 'react';
import { IBit } from 'types/src/types';

export type IUISummaryBit = {
  id: number;
  hasAccess: boolean;
  author: {
    name: string;
    avatarUrl: string;
    uniqueLink: string;
    id: number;
    isPartner: boolean;
  };
  createdAt: string;
  updatedAt: string;
  title: string;
  likes: { count: number };
  commentsCount: number;
  coverImageUrl: string;
  tags: string[];
  slug: string;
  visibility: string;
  isStripeConnected?: boolean;
};

type Props = {
  bits: IUISummaryBit[] | IBit[];
  loading: boolean;
  hasNextPage: boolean;
  loadMore: () => void;
  loadMoreRef: RefObject<HTMLDivElement | null>;
  activeTab?: 'discover' | 'followed' | 'subscribed';
  isProfile?: boolean;
};

export const Bits = ({
  bits,
  loading,
  hasNextPage,
  loadMore,
  loadMoreRef,
  activeTab,
  isProfile,
}: Props) => {
  if (loading) {
    return (
      <div className="flex flex-wrap gap-5 w-full mb-8">
        {Array.from({ length: 9 }).map((_, index) => (
          <Skeleton key={index} className="w-[400px] h-[340px] bg-bg-gray" />
        ))}
      </div>
    );
  }

  if (bits.length === 0) {
    return (
      <div className="text-center py-12">
        {isProfile ? (
          <p className="text-gray-600">User doesn't have any stories yet</p>
        ) : (
          <p className="text-gray-600">No content found</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          'flex flex-wrap gap-2 md: items-center lg:items-start w-full',
          {
            'xl:max-w-[1220px]':
              activeTab === 'discover' || activeTab === undefined,
            'xl:min-w-[904px]': isProfile,
          }
        )}
      >
        <BitsList bits={bits as IBit[]} />
      </div>

      {hasNextPage && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          <Button
            size="sm"
            onClick={loadMore}
            className="px-6 py-3 rounded-lg transition-colors"
          >
            Load More
          </Button>
        </div>
      )}

      {!hasNextPage && bits.length > 0 && (
        <div className="text-center py-8 text-gray-500">
          You've reached the end of the content
        </div>
      )}
    </>
  );
};

export const BitsList = ({ bits }: { bits: IBit[] }) => {
  return (
    <>
      {bits.map((bit) =>
        !bit.hasAccess ? (
          <BitLockedCard
            key={bit.id}
            authorName={bit.author.name}
            isPartner={bit.author.isPartner}
            authorAvatar={bit.author.avatarUrl}
            createdAt={new Date(bit.createdAt).toISOString()}
            title={bit.title}
            likes={bit.likes.count}
            comments={bit.commentsCount}
            image={bit.coverImageUrl}
            authorLink={bit.author.uniqueLink}
            id={bit.id}
            isBitsExplore={true}
            creatorId={bit.author.id}
            isStripeConnected={bit.isStripeConnected}
          />
        ) : (
          <BitCard
            key={bit.id}
            tags={bit.tags}
            isPartner={bit.author.isPartner}
            authorName={bit.author.name}
            authorAvatar={bit.author.avatarUrl}
            createdAt={new Date(bit.createdAt).toISOString()}
            title={bit.title}
            likes={bit.likes.count}
            comments={bit.commentsCount}
            image={bit.coverImageUrl}
            authorLink={bit.author.uniqueLink}
            id={bit.id}
            isBitsExplore={true}
            slug={bit.slug}
            visibility={bit.visibility}
          />
        )
      )}
    </>
  );
};
